import type { QueueRankConfig } from "@rivaled/shared";

import { cn } from "../../lib/utils";
import { PlayerCard } from "../../match-flow/components/PlayerCard";
import { TeamHeader } from "../../match-flow/components/TeamHeader";
import type { PartyColor } from "../../match-flow/lib/party-colors";
import type { MatchPlayer } from "./matchHelpers";
import { toFlowPlayer } from "./matchHelpers";

type TeamCardProps = {
  teamNum: number;
  players: MatchPlayer[];
  teamName: string;
  isMyTeam: boolean;
  avgElo: number;
  currentUserId: string;
  rankConfig: QueueRankConfig | null;
  getPartyColor: (partyId: string | null) => PartyColor | null;
};

export function TeamCard({
  players,
  teamName,
  isMyTeam,
  avgElo,
  currentUserId,
  rankConfig,
  getPartyColor: getPartyColorFn,
}: TeamCardProps) {
  return (
    <div
      className={cn(
        "rounded-xl border overflow-hidden",
        isMyTeam
          ? "bg-surface-800/60 border-brand-500/25"
          : "bg-surface-800/40 border-surface-700/40",
      )}
    >
      <TeamHeader
        teamName={teamName}
        isMyTeam={isMyTeam}
        rightContent={
          <span className="text-[11px] font-display font-semibold text-surface-500 tabular-nums">
            AVG {avgElo}
          </span>
        }
      />
      <div className="p-2 space-y-0.5">
        {players.map((player) => (
          <PlayerCard
            key={player.userId}
            player={toFlowPlayer(player)}
            variant="compact"
            isMe={player.userId === currentUserId}
            rankConfig={rankConfig}
            partyColor={getPartyColorFn(player.partyId)}
            gameIdentity={player.gameIdentity}
          />
        ))}
      </div>
    </div>
  );
}

export function VsDivider() {
  return (
    <div className="flex items-center justify-center py-3 md:py-0 md:px-2">
      <div className="relative flex items-center justify-center w-11 h-11">
        <div className="absolute inset-0 rounded-full bg-brand-500/[0.06] blur-xl" />
        <div className="relative w-9 h-9 rotate-45 rounded-[3px] border border-surface-600/50 bg-surface-850 flex items-center justify-center shadow-brand-glow-sm">
          <span className="font-display text-[10px] font-black text-surface-500 -rotate-45 tracking-[0.25em]">
            VS
          </span>
        </div>
      </div>
    </div>
  );
}
